import * as React from 'react';
import { EVENTS } from 'analytics/analytics-events';
import mixpanel from 'mixpanel-browser';
import { ACTIVITY_BUFFER, MIN_ZOOM_RANGE_MS } from '../support';

import { useActivityEditor } from './use-activity-editor';
import { usePrevious } from 'shared';
import { GraphTool, GraphToolEnum, GraphType, GraphTypeEnum } from './types';
import HighchartsReact from 'highcharts-react-official';

export interface ChartContextShape {
    chartRef: React.RefObject<HighchartsReact.RefObject>;
    graphRange: [number, number];
    chartZoom: [number, number];
    setChartZoom: React.Dispatch<React.SetStateAction<[number, number]>>;
    resetZoom: () => void;
    graphedDeviceIds: number[];
    setGraphedDeviceIds: React.Dispatch<React.SetStateAction<number[] | undefined>>;
    setGraphTool: React.Dispatch<React.SetStateAction<GraphToolEnum>>;
    graphTool: GraphToolEnum;
    setGraphType: React.Dispatch<React.SetStateAction<GraphTypeEnum>>;
    graphType: GraphTypeEnum;
    handleChartSelection: (e: Highcharts.ChartSelectionContextObject, source: string) => boolean;
    handleChartClick: (e: Highcharts.PointerEventObject, source: string) => boolean;
    getXAxisPointerValue: (pointerX: number) => number;
}

const noProviderError = () => {
    console.error('No ChartContextProvider is present');
};

const ChartContext = React.createContext<ChartContextShape>({
    chartRef: React.createRef<HighchartsReact.RefObject>(),
    graphRange: [0, 0],
    chartZoom: [0, 0],
    setChartZoom: noProviderError,
    resetZoom: noProviderError,
    graphedDeviceIds: [],
    setGraphedDeviceIds: noProviderError,
    setGraphTool: noProviderError,
    graphTool: GraphTool.zoomIn,
    setGraphType: noProviderError,
    graphType: GraphType.individual,
    handleChartSelection: () => false,
    handleChartClick: () => false,
    getXAxisPointerValue: () => 0,
});

export const ZOOM_MODIFIER_KEY = 'altKey';

export const ChartContextProvider: React.FunctionComponent = ({ children }) => {
    //Creating reference to chart
    const chartRef = React.useRef<HighchartsReact.RefObject>(null);
    const { activity, athletePanelCards, periods, handlePeriodCreate } = useActivityEditor();
    const [chartZoom, actualSetChartZoom] = React.useState<[number, number]>([0, 0]);
    const [graphedDeviceIds, setGraphedDeviceIds] = React.useState<number[]>();
    const [graphTool, setGraphTool] = React.useState<GraphToolEnum>(GraphTool.zoomIn);
    const [graphType, setGraphType] = React.useState<GraphTypeEnum>(GraphType.individual);
    const [effectiveGraphTool, setEffectiveGraphTool] = React.useState(graphTool);

    const graphRange = React.useMemo((): [number, number] => {
        /**
         * On first load the periods are empty, so we need to return a range of 0,0.
         * Calculate the graph range on empty periods will cause an Infinity range and break the UI
         */
        if (periods.length) {
            const lowestValue = Math.min(...periods.map((period) => period.startDateTime.getTime()));
            const highestValue = Math.max(...periods.map((period) => period.endDateTime.getTime()));
            const buffer = Math.ceil((highestValue - lowestValue) * ACTIVITY_BUFFER);
            return [lowestValue - buffer, highestValue + buffer];
        }
        return [0, 0];
    }, [periods]);

    const setChartZoom: React.Dispatch<React.SetStateAction<[number, number]>> = React.useCallback(
        (newChartZoom) => {
            let myChartZoom: [number, number];
            if (typeof newChartZoom === 'function') {
                myChartZoom = newChartZoom(chartZoom);
            } else {
                myChartZoom = [...newChartZoom];
            }

            // Constrain zoom to a minimum range of 10 seconds and to be inside the graph range

            // If outside to the left, adjust to the right
            if (myChartZoom[0] < graphRange[0]) {
                myChartZoom[1] += graphRange[0] - myChartZoom[0];
                myChartZoom[0] = graphRange[0];
            }

            // If outside to the right, adjust to the left
            if (myChartZoom[1] > graphRange[1]) {
                myChartZoom[0] -= myChartZoom[1] - graphRange[1];
                myChartZoom[1] = graphRange[1];
            }

            let currentRange = myChartZoom[1] - myChartZoom[0];

            // If zoom is too small, move the limits to the min range
            if (currentRange < MIN_ZOOM_RANGE_MS) {
                const adjustBy = (MIN_ZOOM_RANGE_MS - currentRange) / 2;
                myChartZoom[1] += adjustBy;
                myChartZoom[0] -= adjustBy;
            }

            // Ensure we're still inside the graph range
            myChartZoom[0] = Math.max(myChartZoom[0], graphRange[0]);
            myChartZoom[1] = Math.min(myChartZoom[1], graphRange[1]);

            currentRange = myChartZoom[1] - myChartZoom[0];

            // If zoom is too small, move the limits to the min range
            if (currentRange < MIN_ZOOM_RANGE_MS) {
                const adjustBy = (MIN_ZOOM_RANGE_MS - currentRange) / 2;
                myChartZoom[1] += adjustBy;
                myChartZoom[0] -= adjustBy;
            }

            actualSetChartZoom(myChartZoom);
        },
        [chartZoom, graphRange],
    );

    const handleChartSelection = (e: Highcharts.ChartSelectionContextObject, source: string): boolean => {
        switch (effectiveGraphToolRef.current) {
            case GraphTool.drawPeriod:
                handlePeriodCreate('Draw Period', new Date(e.xAxis[0].min), new Date(e.xAxis[0].max));
                break;
            case GraphTool.zoomIn:
                const newChartZoom: typeof chartZoom = [e.xAxis[0].min, e.xAxis[0].max];
                setChartZoom(newChartZoom);
                mixpanel.track(EVENTS.editing.zoomOnSelection, { source });
                break;
        }
        e.preventDefault();
        return false;
    };

    const getXAxisPointerValue = React.useCallback(
        (pointerX: number): number => chartRef.current?.chart?.xAxis[0].toValue(pointerX) || 0,
        [chartRef],
    );

    const handleChartClick = React.useCallback(
        (e: Highcharts.PointerEventObject, source: string) => {
            const doZoom = (zoomFactor: number) => {
                const newZoomRange = Math.ceil((chartZoom[1] - chartZoom[0]) / zoomFactor);
                const clickX = getXAxisPointerValue(e.offsetX);

                setChartZoom([clickX - newZoomRange / 2, clickX + newZoomRange / 2]);
                e.preventDefault();

                mixpanel.track(EVENTS.editing.zoomOnClick, {
                    ZoomType: zoomFactor > 1 ? 'In' : 'Out',
                    source,
                });
            };

            switch (effectiveGraphToolRef.current) {
                case GraphTool.zoomIn:
                    doZoom(2);

                    break;
                case GraphTool.zoomOut:
                    doZoom(0.5);

                    break;
            }
            return false;
        },
        [chartZoom, getXAxisPointerValue, setChartZoom],
    );

    const resetZoom = React.useCallback(() => {
        if (activity) {
            actualSetChartZoom(graphRange);
            mixpanel.track(EVENTS.editing.zoomReset);
        }
    }, [activity, graphRange, actualSetChartZoom]);

    // Reset when switching activities
    const prevActivityId = usePrevious(activity?.id);
    React.useEffect(() => {
        if (prevActivityId !== activity?.id) {
            setGraphedDeviceIds(undefined);
        }
    }, [prevActivityId, activity?.id]);

    // Set state when shift pressed
    const [modifierPressed, setModifierPressed] = React.useState(false);
    React.useEffect(() => {
        const handleModifier = (event: KeyboardEvent) => {
            setModifierPressed(event[ZOOM_MODIFIER_KEY]);
        };

        window.addEventListener('keydown', handleModifier);
        window.addEventListener('keyup', handleModifier);

        // Cleanup
        return () => {
            window.removeEventListener('keydown', handleModifier);
            window.removeEventListener('keyup', handleModifier);
        };
    }, []);

    // Set effective graph tool based on graph type as well as selected graph tool
    // Set graph tool ref so selection handler can reference it without needing to redefine the handler
    const effectiveGraphToolRef = React.useRef(graphTool);
    React.useEffect(() => {
        effectiveGraphToolRef.current = graphTool;

        if (graphTool === GraphTool.rotation && graphType === GraphType.overlapped) {
            effectiveGraphToolRef.current = GraphTool.zoomIn;
        }

        if (effectiveGraphToolRef.current === GraphTool.zoomIn && modifierPressed) {
            effectiveGraphToolRef.current = GraphTool.zoomOut;
        }

        setEffectiveGraphTool(effectiveGraphToolRef.current);
    }, [graphTool, graphType, modifierPressed]);

    return (
        <ChartContext.Provider
            value={{
                chartRef,
                chartZoom,
                setChartZoom,
                resetZoom,
                graphRange,
                graphedDeviceIds:
                    graphedDeviceIds || (athletePanelCards?.[0]?.deviceId ? [athletePanelCards[0].deviceId] : []),
                setGraphedDeviceIds,
                setGraphTool,
                graphTool: effectiveGraphTool,
                setGraphType,
                graphType,
                handleChartSelection,
                handleChartClick,
                getXAxisPointerValue,
            }}
        >
            {children}
        </ChartContext.Provider>
    );
};

export const useChartContext = () => React.useContext(ChartContext);
